import { useEffect, useMemo, useState } from "react";
import { ANNOUNCE_TEMPLATES, renderAnnouncementEmail } from "../../supabase/functions/_shared/announceEmail.js";
import { announceEmail } from "../lib/api.js";

// The 📧 Email part of 超級管理員 → 公告. Template picker, the fields that
// template uses, a preview rendered by the SAME renderer the announce-email
// function sends with, a test send to the admin, and the recipient count.
//
// value: { template, subject, preheader, headline, body, ctaLabel, ctaUrl,
//          items[], offer, offerNote, code, expiry, when }
// fallback: { title, body } — the in-app announcement, used when the email's
// own subject / headline / body are left empty.

export function effectiveAnnounceEmail(value, fallback) {
  const v = value || {};
  return {
    ...v,
    template: v.template || "general",
    subject: v.subject || fallback?.title || "",
    headline: v.headline || fallback?.title || "",
    body: v.body || fallback?.body || "",
  };
}

const box = { width: "100%", boxSizing: "border-box", border: "2px solid var(--c-light)", borderRadius: 14, padding: "11px 14px", fontSize: 14, fontWeight: 600, outline: "none", color: "#4A4A68", background: "#FFFDFD", fontFamily: "inherit" };
const lbl = { fontSize: 12, color: "#8A8A9E", margin: "0 0 5px", fontWeight: 800 };

function Field({ label, value, onChange, placeholder, area, rows = 4 }) {
  return (
    <div>
      <p style={lbl}>{label}</p>
      {area
        ? <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows} style={{ ...box, resize: "vertical", lineHeight: 1.5 }} />
        : <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={box} />}
    </div>
  );
}

export default function AnnouncementEmailEditor({ value, onChange, fallback, toast }) {
  const v = value || {};
  const set = (patch) => onChange({ ...v, ...patch });
  const tplKey = v.template || "general";
  const tpl = ANNOUNCE_TEMPLATES[tplKey];
  const [preview, setPreview] = useState(false);
  const [testing, setTesting] = useState(false);
  const [count, setCount] = useState(null);

  useEffect(() => {
    let alive = true;
    announceEmail("count").then((r) => { if (alive) setCount(r); }).catch(() => { if (alive) setCount({ error: true }); });
    return () => { alive = false; };
  }, []);

  const pickTemplate = (key) => {
    // Switching template swaps in its sample only where a field is empty or
    // still holds the previous template's sample — what the admin typed stays.
    const s = ANNOUNCE_TEMPLATES[key].sample;
    const old = ANNOUNCE_TEMPLATES[tplKey].sample;
    const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
    const next = { ...v, template: key };
    for (const k of new Set([...Object.keys(s), ...Object.keys(old)])) {
      const cur = next[k];
      const untouched = cur == null || cur === "" || (Array.isArray(cur) && !cur.length) || same(cur, old[k]);
      if (untouched) next[k] = s[k] == null ? "" : JSON.parse(JSON.stringify(s[k]));
    }
    onChange(next);
  };
  const loadSample = () => {
    const s = ANNOUNCE_TEMPLATES[tplKey].sample;
    onChange({ template: tplKey, ...JSON.parse(JSON.stringify(s)) });
  };

  const eff = effectiveAnnounceEmail(v, fallback);
  const rendered = useMemo(() => renderAnnouncementEmail(eff, { shopName: "毛毛美容店", unsubscribeUrl: "https://pettobook.com/#unsubscribe-preview" }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(eff)]);

  const sendTest = async () => {
    if (!eff.subject) { toast?.("請先填寫主旨", "error"); return; }
    setTesting(true);
    try {
      const r = await announceEmail("test", eff);
      toast?.(`測試郵件已寄到 ${r.to} 📧`);
    } catch (e) { toast?.(e.message || "寄送失敗", "error"); }
    finally { setTesting(false); }
  };

  const items = Array.isArray(v.items) ? v.items : [];
  const setItem = (i, patch) => set({ items: items.map((x, j) => (j === i ? { ...x, ...patch } : x)) });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <p style={lbl}>範本</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {Object.entries(ANNOUNCE_TEMPLATES).map(([key, t]) => {
            const on = key === tplKey;
            return (
              <button key={key} type="button" onClick={() => pickTemplate(key)}
                style={{ padding: "12px 10px", borderRadius: 14, cursor: "pointer", textAlign: "left", fontWeight: 800, fontSize: 13,
                  border: `2px solid ${on ? t.color : "var(--c-light)"}`, background: on ? t.tint : "#fff", color: on ? t.color : "#4A4A68" }}>
                <span style={{ display: "block", height: 6, borderRadius: 99, marginBottom: 8, background: `linear-gradient(90deg,${t.color},${t.color2})` }} />
                {t.label}
              </button>
            );
          })}
        </div>
        <button type="button" onClick={loadSample} style={{ marginTop: 6, background: "none", border: "none", color: "var(--c-primary)", fontSize: 12, fontWeight: 800, cursor: "pointer", padding: 0 }}>
          ↺ 載入「{tpl.label}」範例內容（會取代以下內容）
        </button>
      </div>

      <Field label="郵件主旨 Subject" value={v.subject} onChange={(x) => set({ subject: x })} placeholder={fallback?.title || "例：PettoBook 新功能上線"} />
      <Field label="預覽文字（收件箱主旨下面嗰行，可留空）" value={v.preheader} onChange={(x) => set({ preheader: x })} placeholder="一句吸引人打開嘅說明" />
      <Field label="大標題" value={v.headline} onChange={(x) => set({ headline: x })} placeholder={fallback?.title || "同主旨一樣"} />
      <Field label="內文（空一行 = 新段落）" area rows={5} value={v.body} onChange={(x) => set({ body: x })} placeholder={fallback?.body || "內文"} />

      {tplKey === "feature" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <p style={{ ...lbl, margin: 0 }}>功能列表</p>
          {items.map((it, i) => (
            <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-start", background: tpl.tint, borderRadius: 14, padding: 8 }}>
              <input value={it.icon || ""} onChange={(e) => setItem(i, { icon: e.target.value })} style={{ ...box, width: 48, padding: "10px 6px", textAlign: "center" }} aria-label="圖示" />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                <input value={it.title || ""} onChange={(e) => setItem(i, { title: e.target.value })} placeholder="功能名稱" style={box} />
                <input value={it.desc || ""} onChange={(e) => setItem(i, { desc: e.target.value })} placeholder="一句說明" style={box} />
              </div>
              <button type="button" onClick={() => set({ items: items.filter((_, j) => j !== i) })} aria-label="刪除" style={{ background: "#FEF2F2", border: "none", borderRadius: 99, width: 30, height: 30, cursor: "pointer", color: "#EF4444", fontWeight: 900 }}>×</button>
            </div>
          ))}
          {items.length < 6 && <button type="button" onClick={() => set({ items: [...items, { icon: "✨", title: "", desc: "" }] })} style={{ ...box, cursor: "pointer", fontWeight: 800, color: "var(--c-primary)", borderStyle: "dashed" }}>＋ 新增功能</button>}
        </div>
      )}
      {tplKey === "promotion" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <Field label="優惠（大字）" value={v.offer} onChange={(x) => set({ offer: x })} placeholder="8 折" />
          <Field label="優惠說明" value={v.offerNote} onChange={(x) => set({ offerNote: x })} placeholder="首 3 個月" />
          <Field label="優惠碼（可留空）" value={v.code} onChange={(x) => set({ code: x })} placeholder="GROOM20" />
          <Field label="有效期" value={v.expiry} onChange={(x) => set({ expiry: x })} placeholder="優惠有效期至…" />
        </div>
      )}
      {tplKey === "notice" && (
        <Field label="時間" value={v.when} onChange={(x) => set({ when: x })} placeholder="2026 年 10 月 20 日 凌晨 2:00 – 2:30" />
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 8 }}>
        <Field label="按鈕文字（可留空）" value={v.ctaLabel} onChange={(x) => set({ ctaLabel: x })} placeholder="立即試用" />
        <Field label="按鈕連結" value={v.ctaUrl} onChange={(x) => set({ ctaUrl: x })} placeholder="https://pettobook.com" />
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" onClick={() => setPreview(true)} style={{ flex: 1, padding: "12px", borderRadius: 99, border: `2px solid ${tpl.color}`, background: "#fff", color: tpl.color, fontWeight: 900, fontSize: 14, cursor: "pointer" }}>👁 預覽郵件</button>
        <button type="button" onClick={sendTest} disabled={testing} style={{ flex: 1, padding: "12px", borderRadius: 99, border: "2px solid var(--c-light)", background: "#fff", color: "#4A4A68", fontWeight: 900, fontSize: 14, cursor: "pointer", opacity: testing ? 0.6 : 1 }}>{testing ? "寄送中…" : "📨 寄測試郵件給我"}</button>
      </div>
      <p style={{ fontSize: 12, color: "#8A8A9E", margin: 0, fontWeight: 700, lineHeight: 1.5 }}>
        {count == null ? "計算收件人…" : count.error ? "未能讀取收件人數目" : `📬 發布時會寄給 ${count.count} 位店主${count.optedOut ? `（${count.optedOut} 位已取消訂閱，不會收到）` : ""}。每封都有「取消訂閱」連結。`}
      </p>

      {preview && <EmailPreview rendered={rendered} preheader={eff.preheader} onClose={() => setPreview(false)} />}
    </div>
  );
}

function EmailPreview({ rendered, preheader, onClose }) {
  const [w, setW] = useState("desktop");
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div role="dialog" aria-modal="true" aria-label="郵件預覽" onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 9500, background: "rgba(20,18,40,0.72)", display: "flex", flexDirection: "column", alignItems: "center", padding: "16px 12px" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: w === "desktop" ? 720 : 400, display: "flex", flexDirection: "column", flex: 1, minHeight: 0, transition: "max-width .2s" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          {[["desktop", "🖥 電腦"], ["mobile", "📱 手機"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setW(k)} style={{ padding: "8px 14px", borderRadius: 99, border: "none", fontWeight: 800, fontSize: 13, cursor: "pointer", background: w === k ? "#fff" : "rgba(255,255,255,0.18)", color: w === k ? "#1F1B2E" : "#fff" }}>{l}</button>
          ))}
          <span style={{ flex: 1 }} />
          <button type="button" onClick={onClose} aria-label="關閉" style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: "rgba(255,255,255,0.18)", color: "#fff", fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ background: "#fff", borderRadius: "14px 14px 0 0", padding: "12px 16px", borderBottom: "1px solid #EEE" }}>
          <div style={{ fontSize: 12, color: "#8A8A9E", fontWeight: 700 }}>PettoBook &lt;noreply@pettobook.com&gt;</div>
          <div style={{ fontSize: 15, color: "#1F1B2E", fontWeight: 900, marginTop: 2 }}>{rendered.subject}</div>
          {preheader && <div style={{ fontSize: 13, color: "#8A8A9E", fontWeight: 600, marginTop: 2 }}>{preheader}</div>}
        </div>
        <iframe title="郵件預覽" srcDoc={rendered.html} sandbox="allow-popups allow-popups-to-escape-sandbox"
          style={{ flex: 1, minHeight: 0, width: "100%", border: "none", background: "#F4F2F8", borderRadius: "0 0 14px 14px" }} />
      </div>
    </div>
  );
}
